import { type FC, useMemo, useState } from "react";

import { Button, Colors } from "@snapchat/snap-design-system";
import { AddIcon } from "@snapchat/snap-design-system-icons";

import { useFeatureGates } from "../../../../cof-feature-gates/hooks";
import { CofConfigId } from "../../../../gql-generated/types";
import { PageHeader } from "../../../../layout/components";
import { Flexbox, FormModal } from "../../../../shared/components";
import { useSelectedLensCentralOrganizationNotIncludesCamkitOrgs, useToggleState } from "../../../../shared/hooks";
import { useCreateLensFolder } from "../../../../shared/hooks/useCreateLensFolder";
import { useSelectedOrganization } from "../../../../shared/hooks/useSelectedOrganization";
import {
    LensSpaceTextInputSection,
    createTextInputHandler,
    pageContent,
} from "../../../../submit/components/lens-space";
import { CreateLensFolder, CreateLensFolderHeader } from "../../../../submit/components/lens-space/messages.generated";
import { isInvalidTextInput } from "../../../../submit/validation";

import { createLensFolderModalStyles } from "./AddLensFolder.styles";
import { CreateFolder } from "./messages.generated";

export const AddFolderModal: FC = () => {
    const [folderModalProps, showFolderModal] = useToggleState();
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const selectedOrganization = useSelectedLensCentralOrganizationNotIncludesCamkitOrgs();

    const selectedOrganizationV2 = useSelectedOrganization();

    const orgId = isMyLensesOrgsV2Enabled ? selectedOrganizationV2?.id : selectedOrganization?.id;

    const [lensFolderName, setLensFolderName] = useState("");
    const handleLensFolderNameChange = useMemo(() => createTextInputHandler(setLensFolderName), [setLensFolderName]);

    const { createLensFolder, loading: creatingLensFolder } = useCreateLensFolder(orgId ?? "", lensFolderName);

    const isLensSpaceNameInvalid = isInvalidTextInput(lensFolderName);
    const isCreateLensFolderButtonDisabled = creatingLensFolder || isLensSpaceNameInvalid;

    const handleAddFolder = () => {
        void createLensFolder().then(() => {
            folderModalProps.onCancel();
            setLensFolderName("");
        });
    };
    return (
        <>
            <Button data-testid="lens-folders-header-create-button" onClick={showFolderModal} type="primary">
                <AddIcon fill={Colors.WHITE} />
                <CreateFolder />
            </Button>
            <FormModal
                dataTestId="lens-folders-modal"
                footer={
                    <Flexbox justifyContent="flex-end" gap="5%">
                        <Button
                            data-testid="lens-folders-modal-cancel-button"
                            onClick={folderModalProps.onCancel}
                            isDisabled={creatingLensFolder}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="lens-folders-modal-create-button"
                            type="primary"
                            onClick={handleAddFolder}
                            isDisabled={isCreateLensFolderButtonDisabled}
                            isPending={creatingLensFolder}
                        >
                            <CreateLensFolder />
                        </Button>
                    </Flexbox>
                }
                modalProps={folderModalProps}
                className={createLensFolderModalStyles}
            >
                <PageHeader>
                    <CreateLensFolderHeader />
                </PageHeader>
                <Flexbox flexDirection="column" className={pageContent}>
                    <LensSpaceTextInputSection
                        inputValue={lensFolderName}
                        onChange={handleLensFolderNameChange}
                        isLoading={creatingLensFolder}
                    />
                </Flexbox>
            </FormModal>
        </>
    );
};
